import "./LoginPage.scss";

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import Header from "@elements/Header";
import useDataLayer from "@hooks/useDataLayer";
import { useQuery } from "@hooks/useQuery";
import { useTitle } from "@hooks/useTitle";
import {
  Button,
  Feedback,
  FormGroup,
  Label,
  Link,
  Text,
  TextField,
  Title,
} from "@runwayhealth/runway-components-react";
import { LOGIN, LogoutReasons } from "@store/user/userActions";
import { GA4Events } from "@types-runway/analytics";
import { RootEntity, RootState } from "@types-runway/state";
import { User } from "@types-runway/user";

type LoginField = "email" | "password";

interface LoginError {
  message: string;
  fields: LoginField[];
}

const noLoginError: LoginError = { message: "", fields: [] };

interface LoginLocationState {
  logoutReason?: LogoutReasons;
}

// Reads the browser's own constraint checks (required, type="email") so the
// page can show a persistent message instead of the native popup.
const findInvalidField = (form: HTMLFormElement): LoginError | undefined => {
  const email = form.elements.namedItem("email") as HTMLInputElement;
  const password = form.elements.namedItem("password") as HTMLInputElement;

  if (email.validity.valueMissing) {
    return { message: "Enter your email address.", fields: ["email"] };
  }
  if (email.validity.typeMismatch) {
    return { message: "Enter an email address like name@example.com.", fields: ["email"] };
  }
  if (password.validity.valueMissing) {
    return { message: "Enter your password.", fields: ["password"] };
  }
  return undefined;
};

const LoginPage = () => {
  const history = useHistory();
  const location = useLocation<LoginLocationState | undefined>();
  const hasSessionExpired = location.state?.logoutReason === LogoutReasons.SESSION_EXPIRED;
  const dispatch = useDispatch();
  const query = useQuery();
  const { reportAccess } = useDataLayer(GA4Events.LOGIN);
  const user = useSelector<RootState, RootEntity<User>>((state) => state.user);
  const [startedQuestionnaire, setStartedQuestionnaire] = useState(false);

  const [loginState, setLoginState] = useState({
    email: user.data.email || "",
    password: "",
  });

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [loginError, setLoginError] = useState<LoginError>(noLoginError);
  useTitle("Login");

  useMemo(() => {
    // Load start questionnaire state
    const startQuestionnaire = query.get("startQuestionnaire");
    if (Boolean(startQuestionnaire) && startQuestionnaire === "true") {
      setStartedQuestionnaire(true);
    }
  }, []);

  useEffect(() => {
    if (user.data.id && localStorage.getItem("token")) {
      // Report login analytics
      reportAccess();
    }
  }, [user.data.id]);

  // Only errors from this page's own submit; user.error is persisted across reloads.
  // hasSubmitted is left out of the deps: it turns true before any such error arrives.
  useEffect(() => {
    if (hasSubmitted && user.error.message) {
      setLoginError({ message: user.error.message, fields: ["email", "password"] });
    }
  }, [user.error]);

  const errorPropsFor = (field: LoginField) => {
    const isInvalid = loginError.fields.includes(field);
    return {
      isInvalid,
      "aria-invalid": isInvalid,
      "aria-describedby": isInvalid ? "login-error" : undefined,
    };
  };

  const authorization = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // The loading button only blocks clicks; Enter in a field still submits.
    if (user.isPending) {
      return;
    }
    const form = event.currentTarget;
    const invalidField = findInvalidField(form);
    if (invalidField) {
      setLoginError(invalidField);
      (form.elements.namedItem(invalidField.fields[0]) as HTMLInputElement).focus();
      return;
    }
    setHasSubmitted(true);
    dispatch({
      type: LOGIN,
      email: loginState.email,
      password: loginState.password,
      startQuestionnaire: startedQuestionnaire,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginState((prev) => ({ ...prev, [name]: value }));
    setLoginError(noLoginError);
  };

  return (
    <div className="login-screen">
      <Header
        previous={() => {
          history.goBack();
        }}
        hasProgressBar={false}
      />
      <main className="login-page">
        <div className="login-page__form">
          <Title size="giant" className="login-page__title">
            Log in to your dashboard
          </Title>
          <Text className="login-page__subtitle">Use your email and password to log in.</Text>
          {hasSessionExpired && (
            <p className="login-page__notice" role="status">
              Your session has ended for your security. Log in again to continue.
            </p>
          )}
          <form onSubmit={authorization} noValidate>
            <div className="login-page__fields">
              <FormGroup>
                <Label htmlFor="login-email">Email</Label>
                <TextField
                  id="login-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={loginState.email}
                  required
                  {...errorPropsFor("email")}
                  onChange={handleChange}
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="login-password">Password</Label>
                <TextField
                  id="login-password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={loginState.password}
                  required
                  {...errorPropsFor("password")}
                  onChange={handleChange}
                />
              </FormGroup>
              {loginError.message && (
                <Feedback id="login-error" role="alert" isInvalid>
                  {loginError.message}
                </Feedback>
              )}
            </div>
            <Button
              size="lg"
              iconName="ArrowRight"
              isLoading={user.isPending}
              isBlock
              type="submit"
            >
              Log in
            </Button>
          </form>
        </div>
        <div className="login-page__links">
          <Link href="/registration">Create account</Link>
          <Link href="/forgot-password">Forgot password?</Link>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
